/** Group flat license rows for dashboard display — one row per org. */

import { isLicenseExpired } from './deviceInfo';

const isOrgLicense = (lic) =>
  lic.customer_type === 'organisation' && lic.organisation_id != null;

export const orgOverallStatus = (licenses) => {
  if (!licenses.length) {
    return { text: 'Unknown', class: 'status-pending' };
  }
  const states = licenses.map((l) => {
    if (!l.device_id) return 'pending';
    if (isLicenseExpired(l)) return 'expired';
    if (l.status === 'active') return 'active';
    if (l.status === 'expired' || l.status === 'inactive' || l.status === 'suspended') {
      return 'expired';
    }
    return l.status || 'pending';
  });
  const allPending = states.every((s) => s === 'pending');
  const allActive = states.every((s) => s === 'active');
  const allExpired = states.every((s) => s === 'expired');
  if (allPending) return { text: 'Pending', class: 'status-pending' };
  if (allActive) return { text: 'Active', class: 'status-active' };
  if (allExpired) return { text: 'Expired', class: 'status-expired' };
  return { text: 'Partial', class: 'status-pending' };
};

export const groupLicensesForDisplay = (licenses) => {
  const individuals = [];
  const orgMap = new Map();

  for (const lic of licenses) {
    if (!isOrgLicense(lic)) {
      individuals.push({ ...lic, _rowType: 'individual', _rowKey: `ind-${lic.id}` });
      continue;
    }
    const orgId = lic.organisation_id;
    if (!orgMap.has(orgId)) {
      orgMap.set(orgId, []);
    }
    orgMap.get(orgId).push(lic);
  }

  const orgRows = [];
  for (const [orgId, orgLicenses] of orgMap.entries()) {
    const first = orgLicenses[0];
    const invalidSum = orgLicenses.reduce(
      (sum, l) => sum + Number(l.invalid_attempt_count || 0),
      0
    );
    const createdAt =
      first.org_created_at ||
      orgLicenses.reduce((earliest, l) => {
        if (!earliest) return l.created_at;
        return new Date(l.created_at) < new Date(earliest) ? l.created_at : earliest;
      }, null);

    orgRows.push({
      _rowType: 'organisation',
      _rowKey: `org-${orgId}`,
      organisation_id: orgId,
      org_name: first.org_name || first.client_name,
      client_name: first.org_name || first.client_name,
      email: first.email,
      phone: first.phone,
      city: first.city,
      clinic_name: first.clinic_name,
      application_name: first.application_name,
      customer_type: 'organisation',
      created_at: createdAt,
      systems: orgLicenses,
      systems_count: orgLicenses.length,
      invalid_attempt_count: invalidSum,
      statusInfo: orgOverallStatus(orgLicenses),
    });
  }

  orgRows.sort(
    (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
  );
  individuals.sort(
    (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
  );

  return [...orgRows, ...individuals];
};

export const matchesLicenseSearch = (row, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  if (row._rowType === 'organisation') {
    return (
      (row.org_name || '').toLowerCase().includes(q) ||
      (row.email || '').toLowerCase().includes(q) ||
      (row.city || '').toLowerCase().includes(q) ||
      row.systems.some(
        (s) =>
          (s.system_name || '').toLowerCase().includes(q) ||
          (s.license_key || '').toLowerCase().includes(q)
      )
    );
  }
  return (
    (row.client_name || '').toLowerCase().includes(q) ||
    (row.email || '').toLowerCase().includes(q) ||
    (row.license_key || '').toLowerCase().includes(q) ||
    (row.device_id && row.device_id.toLowerCase().includes(q)) ||
    (row.mac_address && row.mac_address.toLowerCase().includes(q)) ||
    (row.clinic_name && row.clinic_name.toLowerCase().includes(q)) ||
    (row.city && row.city.toLowerCase().includes(q))
  );
};

export const matchesLicenseFilter = (row, activeFilter) => {
  if (activeFilter === 'all') return true;
  if (row._rowType === 'organisation') {
    const systems = row.systems || [];
    if (activeFilter === 'pending') {
      return systems.some((l) => !l.device_id);
    }
    if (activeFilter === 'active') {
      return systems.some((l) => l.status === 'active' && l.device_id && !isLicenseExpired(l));
    }
    if (activeFilter === 'expired') {
      return systems.some((l) => isLicenseExpired(l));
    }
    if (activeFilter === 'inactive') {
      return systems.some((l) => l.status === activeFilter);
    }
    return systems.some((l) => l.status === activeFilter);
  }
  if (activeFilter === 'pending') return !row.device_id;
  if (activeFilter === 'active') return row.status === 'active' && row.device_id && !isLicenseExpired(row);
  if (activeFilter === 'expired') return isLicenseExpired(row);
  return row.status === activeFilter;
};
